/**
 * 게임 시간 + 슬로모 + 고정 스텝 accumulator (data-model §7, research §1).
 * `now`는 timeScale이 반영된 게임 누적 시각. 콤보 창·파편 수명 등 모든 게임 시간 비교의 기준.
 */
import type { EventBus } from './EventBus';
import { BALANCE } from '../config/balance';

/** 프레임 dt 상한(초). 스파이럴 오브 데스/탭 비활성 후 큰 점프 방지 — Clock과 loop 공용. */
export const MAX_FRAME_DT = 0.1;

export class Clock {
  now = 0;
  timeScale = 1.0;
  private slowmoUntil: number | null = null;
  /** 이 게임 시각 전까지는 슬로모를 막는다(책상 뒤집기 연출 보호). 0이면 억제 없음. */
  private slowmoSuppressedUntil = 0;
  private accumulator = 0;

  constructor(private bus: EventBus) {
    // 3콤보 이상 파괴 순간에만 슬로모 (FR-007).
    this.bus.on('combo:slowmo', () => this.triggerSlowmo());
    // 책상 뒤집기: 발사된 물건의 연쇄 파괴로 슬로모가 걸려 빠른 뒤집기 연출이 느려지지 않게,
    // 진행 중 슬로모를 끄고 일정 시간 재발동을 막는다(일반 반파는 flip=false라 영향 없음).
    this.bus.on('desk:destroyed', (e) => {
      if (e.flip) this.suppressSlowmo();
    });
    // 리셋/세션 종료 시 슬로모·억제 상태 즉시 해제.
    this.bus.on('session:reset', () => this.reset());
    this.bus.on('session:end', () => this.reset());
  }

  private triggerSlowmo(): void {
    if (this.now < this.slowmoSuppressedUntil) return; // 뒤집기 연출 중엔 무시.
    this.timeScale = BALANCE.slowmo.timeScale;
    this.slowmoUntil = this.now + BALANCE.slowmo.durationSec;
  }

  private suppressSlowmo(): void {
    this.clearSlowmo();
    this.slowmoSuppressedUntil = this.now + BALANCE.rage.flipNoSlowmoSec;
  }

  private clearSlowmo(): void {
    this.timeScale = 1.0;
    this.slowmoUntil = null;
  }

  private reset(): void {
    this.clearSlowmo();
    this.slowmoSuppressedUntil = 0;
  }

  /**
   * 프레임 진행. rawDt(초)를 받아 게임 시간을 전진시키고 고정 스텝 횟수를 반환.
   * @returns 이번 프레임에 실행할 물리 스텝 수
   */
  advance(rawDt: number): number {
    // 스파이럴 오브 데스 방지: 프레임 dt를 [0, MAX_FRAME_DT]로 클램프 후 timeScale 적용.
    // (음수 dt가 들어오면 게임 시간이 역행해 콤보/수명/쿨다운이 전부 깨진다.)
    const frameDt = Math.max(0, Math.min(rawDt, MAX_FRAME_DT)) * this.timeScale;
    this.now += frameDt;

    if (this.slowmoUntil !== null && this.now >= this.slowmoUntil) {
      this.clearSlowmo();
    }

    this.accumulator += frameDt;
    let steps = 0;
    while (this.accumulator >= BALANCE.fixedDt) {
      this.accumulator -= BALANCE.fixedDt;
      steps++;
      // 극단적 지연 시 무한 루프 방지.
      if (steps >= 5) {
        this.accumulator = 0;
        break;
      }
    }
    return steps;
  }
}
